import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import authService from '@services/authService'
import { NotificationBell } from '@components/features/notifications'
import './Navbar.scss'

/**
 * Navigation Bar Component
 * Features:
 * - Fixed top position
 * - Notification bell
 * - User profile dropdown with logout
 * - Sidebar toggle
 * - Real-time user data from authentication
 */
const Navbar = ({ onToggleSidebar, onToggleSidebarCollapse, sidebarCollapsed, onLogout }) => {
	const navigate = useNavigate()
	const [showProfile, setShowProfile] = useState(false)
	const [currentUser, setCurrentUser] = useState(null)

	const profileRef = useRef(null)

	// Load current user data
	useEffect(() => {
		const user = authService.getCurrentUser()
		setCurrentUser(user)
	}, [])

	// Handle click outside to close dropdowns
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (profileRef.current && !profileRef.current.contains(event.target)) {
				setShowProfile(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	return (
		<nav className="navbar">
			<div className="navbar__left">
				{/* Mobile Menu Toggle */}
				<button
					className="navbar__toggle navbar__toggle--mobile"
					onClick={onToggleSidebar}
					aria-label="Toggle sidebar"
				>
					<i className="icon-menu"></i>
				</button>

				{/* Logo */}
				<div className="navbar__logo">
					<img src="/logo.png" alt="CHUB Logo" className="navbar__logo-image" />
					<span className="navbar__logo-subtitle">Compliance Hub</span>
				</div>

				{/* Desktop Sidebar Collapse Toggle */}
				<button
					className="navbar__toggle navbar__toggle--collapse"
					onClick={onToggleSidebarCollapse}
					aria-label="Collapse sidebar"
				>
					<i className={`icon-${sidebarCollapsed ? 'arrow-right' : 'arrow-left'}`}></i>
				</button>
			</div>

			<div className="navbar__right">
				{/* Notifications */}
				<NotificationBell
					pollingInterval={30000}
					showBadge={true}
					size="medium"
				/>

				{/* User Profile */}
				<div className="navbar__dropdown" ref={profileRef}>
					<button
						className="navbar__user"
						onClick={() => setShowProfile(!showProfile)}
						aria-label="User menu"
					>
						<img
							src={currentUser
								? `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser.first_name || '')}+${encodeURIComponent(currentUser.last_name || '')}&background=5c8ed4&color=fff&size=40`
								: 'https://ui-avatars.com/api/?name=User&background=5c8ed4&color=fff&size=40'
							}
							alt="User Avatar"
							className="navbar__user-avatar"
						/>
						<div className="navbar__user-info">
							<span className="navbar__user-name">
								{currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'User'}
							</span>
							<span className="navbar__user-role">
								{currentUser?.role?.display_name || currentUser?.role?.name || 'User'}
							</span>
						</div>
						<i className="icon-arrow-down navbar__user-arrow"></i>
					</button>

					{showProfile && (
						<div className="navbar__dropdown-menu navbar__dropdown-menu--user">
							<div className="user-dropdown__header">
								<img
									src={currentUser
										? `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser.first_name || '')}+${encodeURIComponent(currentUser.last_name || '')}&background=5c8ed4&color=fff&size=60`
										: 'https://ui-avatars.com/api/?name=User&background=5c8ed4&color=fff&size=60'
									}
									alt="User Avatar"
									className="user-dropdown__avatar"
								/>
								<div>
									<p className="user-dropdown__name">
										{currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'User'}
									</p>
									<p className="user-dropdown__email">
										{currentUser?.email || ''}
									</p>
								</div>
							</div>
							<div className="user-dropdown__menu">
								<button
									onClick={() => {
										setShowProfile(false)
										navigate('/profile')
									}}
									className="user-dropdown__item"
									style={{ width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer' }}
								>
									<i className="icon-user"></i>
									<span>My Profile</span>
								</button>
								<button
									onClick={() => {
										setShowProfile(false)
										navigate('/profile')
									}}
									className="user-dropdown__item"
									style={{ width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer' }}
								>
									<i className="icon-settings"></i>
									<span>Account Settings</span>
								</button>
								<a href="#" className="user-dropdown__item">
									<i className="icon-question"></i>
									<span>Help & Support</span>
								</a>
								<div className="user-dropdown__divider"></div>
								<button
									onClick={(e) => {
										e.preventDefault()
										setShowProfile(false)
										onLogout()
									}}
									className="user-dropdown__item user-dropdown__item--danger"
									style={{ width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer' }}
								>
									<i className="icon-logout"></i>
									<span>Logout</span>
								</button>
							</div>
						</div>
					)}
				</div>
			</div>
		</nav>
	)
}

export default Navbar
